import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'https://seodash360.com';
const API = `${BACKEND_URL}/api`;

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,

      setUser: (user) => set({ user, isAuthenticated: !!user }),
      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
      setLoading: (isLoading) => set({ isLoading }),

      login: async (email, password) => {
        try {
          const response = await fetch(`${API}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });

          const data = await response.json();
          
          if (!response.ok) {
            throw new Error(data.detail || 'Login failed');
          }

          set({
            user: data.user,
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
            isAuthenticated: true,
            isLoading: false,
          });

          return data;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      register: async (email, password, name) => {
        try {
          const response = await fetch(`${API}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, name }),
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.detail || 'Registration failed');
          }

          set({
            user: data.user,
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
            isAuthenticated: true,
            isLoading: false,
          });

          return data;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      checkAuth: async () => {
        const { accessToken } = get();
        if (!accessToken) {
          set({ isLoading: false, isAuthenticated: false });
          return;
        }

        try {
          const response = await fetch(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });

          if (!response.ok) throw new Error('Auth check failed');
          const user = await response.json();
          set({ user, isAuthenticated: true, isLoading: false });
        } catch (error) {
          set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false, isLoading: false });
        }
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get();
        if (!refreshToken) return null;

        try {
          const response = await fetch(`${API}/auth/refresh`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${refreshToken}` },
          });

          if (!response.ok) throw new Error('Token refresh failed');
          const data = await response.json();
          set({ accessToken: data.access_token });
          return data.access_token;
        } catch (error) {
          get().logout();
          return null;
        }
      },

      initiateOAuthLogin: () => {
        window.location.href = 'https://seodash360.com/api/auth/google';
      },
      
      processOAuthCallback: async (sessionId) => {
        try {
          const response = await fetch(`${API}/auth/session`, {
            method: 'GET',
            headers: { 'X-Session-ID': sessionId },
          });

          if (!response.ok) throw new Error('OAuth session exchange failed');
          const data = await response.json();
          set({
            user: {
              user_id: data.user_id,
              email: data.email,
              name: data.name,
              picture: data.picture,
              plan: data.plan,
            },
            accessToken: data.session_token,
            isAuthenticated: true,
            isLoading: false,
          });
          return data;
        } catch (error) {
          throw error;
        }
      },
    }),
    {
      name: 'seodash-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

export const apiClient = {
  async fetch(endpoint, options = {}) {
    const { accessToken, refreshAccessToken } = useAuthStore.getState();
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    let response = await fetch(`${API}${endpoint}`, { ...options, headers });
    if (response.status === 401 && accessToken) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(`${API}${endpoint}`, { ...options, headers });
      }
    }
    return response;
  },
  async get(endpoint) {
    const response = await this.fetch(endpoint);
    const data = await response.json();
    if (!response.ok) {
      const err = new Error(typeof data.detail === 'string' ? data.detail : 'Request failed');
      err.detail = data.detail;
      err.status = response.status;
      throw err;
    }
    return data;
  },
  async post(endpoint, body) {
    const response = await this.fetch(endpoint, { method: 'POST', body: JSON.stringify(body) });
    const data = await response.json();
    if (!response.ok) {
      const err = new Error(typeof data.detail === 'string' ? data.detail : 'Request failed');
      err.detail = data.detail;
      err.status = response.status;
      throw err;
    }
    return data;
  },
  async delete(endpoint) {
    const response = await this.fetch(endpoint, { method: 'DELETE' });
    const data = await response.json();
    if (!response.ok) { const err = new Error(typeof data.detail === 'string' ? data.detail : 'Request failed'); err.detail = data.detail; err.status = response.status; throw err; }
    return data;
  },
};
